import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// @mui
import {
    Link,
    Stack,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Alert,
    Divider
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from 'components/iconify';
import LanguagePopover from 'popovers/LanguagePopover';
// server
import { useStateSelector } from '../../context/StateSelectors';
// i18n
import i18n from 'i18';
// utils
import helper from 'utils/helper';
import { formatError, hasError } from 'utils/formatError';
import { LoginWithApple } from '../../sections/auth/LoginWithApple';
import { LoginWithGoogle } from '../../sections/auth/LoginWithGoogle';
import CodeAuthField from '../../components/code-auth-input/CodeAuthField';
import config from '../../config';
import {
    isKeycloakConfigured,
    isKeycloakEnabled,
    startKeycloakLogin
} from '../../auth/keycloak';

// ----------------------------------------------------------------------

export default function LoginForm(props) {
    const navigate = useNavigate();
    const server = useStateSelector((state) => state.server);

    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [keycloakLoading, setKeycloakLoading] = useState(false);
    const [showCodeAuth, setShowCodeAuth] = useState(false);
    const keycloakAutoRedirectTriggeredRef = useRef(false);
    const keycloakMode = isKeycloakEnabled();
    const keycloakConfigured = isKeycloakConfigured();
    const loginPasswordAuthEnabled =
        config.allowLoginPasswordAuth !== false || !keycloakMode;

    // Autofill check https://github.com/facebook/react/issues/1159#issuecomment-1025423604
    const [wasUsernameAutofilled, setWasUsernameAutofilled] = useState(false);
    const [wasPasswordAutofilled, setWasPasswordAutofilled] = useState(false);

    useLayoutEffect(() => {
        const checkAutofilled = () => {
            const usernameAutofilled = !!document
                .getElementById('username')
                ?.matches('*:-webkit-autofill');
            const passwordAutofilled = !!document
                .getElementById('password')
                ?.matches('*:-webkit-autofill');
            setWasUsernameAutofilled(usernameAutofilled);
            setWasPasswordAutofilled(passwordAutofilled);
        };
        // The time when it's ready is not very stable, so check few times
        setTimeout(checkAutofilled, 200);
        setTimeout(checkAutofilled, 500);
        setTimeout(checkAutofilled, 1000);
        setTimeout(checkAutofilled, 2000);
    }, []);

    const handleLoginClick = async () => {
        setError('');
        setLoading(true);

        let result = await server.auth.loginUser(username, password);
        if (result.error === 'OK') {
            setLoading(false);
            if (result.user) {
                navigate('/app/dashboard', { replace: true });
            } else {
                // user uses 2FA, we need to ask for code
                setShowCodeAuth(true);
            }
        } else {
            setLoading(false);
            // setError(formatError(result));
        }
    };

    const handleTwoFactorAuth = async (code) => {
        setError('');
        setLoading(true);
        const result = await server.totp.check(code);
        if (result.error === 'OK') {
            setLoading(false);
            navigate('/app/dashboard', { replace: true });
        } else {
            setLoading(false);
            setError(formatError(result));
        }
    };

    const handleGoogleSuccess = (google) => {
        server.auth
            .loginWithGoogle(google.clientId, google.credential)
            .then((result) => {
                if (!hasError(result)) {
                    if (!result.user) {
                        // user not registered, we need to select role and accept terms to register
                        navigate('/auth/login-by-google', {
                            state: {
                                clientId: google.clientId,
                                credential: google.credential,
                                ...result
                            }
                        });
                    } else {
                        document.location = '/app/dashboard';
                        //navigate('/app', { replace: true });
                    }
                } else {
                    setError(formatError(result));
                }
            });
    };

    const handleKeycloakLoginClick = async () => {
        setError('');

        if (!keycloakConfigured) {
            setError(
                'Keycloak is not configured. Please set REACT_APP_KEYCLOAK_URL, REACT_APP_KEYCLOAK_REALM and REACT_APP_KEYCLOAK_CLIENT_ID.'
            );
            return;
        }

        setKeycloakLoading(true);

        try {
            const sessionReady = await startKeycloakLogin('/app/dashboard');
            if (sessionReady) {
                navigate('/app/dashboard', { replace: true });
            }
        } catch (e) {
            if (e?.message === 'KEYCLOAK_NOT_CONFIGURED') {
                setError(
                    'Keycloak is not configured. Please set REACT_APP_KEYCLOAK_URL, REACT_APP_KEYCLOAK_REALM and REACT_APP_KEYCLOAK_CLIENT_ID.'
                );
            } else {
                setError('SSO token exchange failed. Please check /api/auth/keycloak.');
            }
        } finally {
            setKeycloakLoading(false);
        }
    };

    useEffect(() => {
        if (loginPasswordAuthEnabled || !keycloakMode) {
            return;
        }

        if (keycloakAutoRedirectTriggeredRef.current) {
            return;
        }

        keycloakAutoRedirectTriggeredRef.current = true;
        handleKeycloakLoginClick();
    }, [loginPasswordAuthEnabled, keycloakMode]);

    // ensuring old session is not used instead of new tokens
    document.cookie = 'JSESSIONID=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

    return (
        <>
            <Helmet>
                <title>{helper.formPageTitle('auth.login')}</title>
            </Helmet>

            {!showCodeAuth && (
                <>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h4" gutterBottom>
                            {i18n.t('auth.loginTitle')}
                        </Typography>

                        <LanguagePopover forceSmall={true} />
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    {loginPasswordAuthEnabled && (
                        <Stack spacing={3}>
                            <TextField
                                id="username"
                                name="username"
                                label={i18n.t('columns.email')}
                                value={username}
                                onKeyDown={(ev) => {
                                    if (
                                        ev.key === 'Enter' &&
                                        password.length > 0
                                    ) {
                                        handleLoginClick();
                                    }
                                }}
                                onChange={(event) =>
                                    setUsername(event.target.value)
                                }
                                InputLabelProps={{
                                    shrink:
                                        wasUsernameAutofilled ||
                                        username.length > 0
                                }}
                            />

                            <TextField
                                id="password"
                                name="password"
                                label={i18n.t('columns.password')}
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(event) =>
                                    setPassword(event.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleLoginClick();
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword
                                                    )
                                                }
                                                edge="end"
                                            >
                                                <Iconify
                                                    icon={
                                                        showPassword
                                                            ? 'eva:eye-fill'
                                                            : 'eva:eye-off-fill'
                                                    }
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                InputLabelProps={{
                                    shrink:
                                        wasPasswordAutofilled ||
                                        password.length > 0
                                }}
                            />
                        </Stack>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {loginPasswordAuthEnabled && (
                        <LoadingButton
                            loading={loading}
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            sx={{ my: 2 }}
                            onClick={handleLoginClick}
                        >
                            {i18n.t('auth.login')}
                        </LoadingButton>
                    )}

                    {keycloakMode && (
                        <LoadingButton
                            loading={keycloakLoading}
                            disabled={!keycloakConfigured}
                            fullWidth
                            size="large"
                            type="button"
                            variant="outlined"
                            sx={{ mb: 2 }}
                            onClick={handleKeycloakLoginClick}
                        >
                            Sign in with Keycloak
                        </LoadingButton>
                    )}

                    {config.allowGoogleAuth && (
                        <LoginWithGoogle
                            onSuccess={(result) => handleGoogleSuccess(result)}
                        />
                    )}

                    <LoginWithApple />

                    <Divider sx={{ my: 2 }} />
                </>
            )}

            {showCodeAuth && (
                <Stack spacing={3}>
                    <Typography variant="h5" gutterBottom>
                        {i18n.t('auth.twoFactorAuth')}
                    </Typography>

                    <CodeAuthField callback={handleTwoFactorAuth} />

                    <Divider sx={{ my: 3 }} />
                </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {showCodeAuth && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ my: 2, cursor: 'pointer' }}
                >
                    <Link
                        variant="subtitle2"
                        underline="hover"
                        onClick={() => setShowCodeAuth(false)}
                    >
                        {i18n.t('auth.backToLogin')}
                    </Link>
                </Stack>
            )}

            {!showCodeAuth && loginPasswordAuthEnabled && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ my: 2, cursor: 'pointer' }}
                >
                    {config.allowMerchantRegistration && (
                        <Link
                            variant="subtitle2"
                            underline="hover"
                            onClick={() => navigate('/auth/register')}
                        >
                            {i18n.t('auth.newMerchantRegisterLink')}
                        </Link>
                    )}
                    <Link
                        variant="subtitle2"
                        underline="hover"
                        onClick={() => navigate('/auth/forgot')}
                    >
                        {i18n.t('auth.forgotPasswordLink')}
                    </Link>
                </Stack>
            )}
        </>
    );
}
